import { money } from "@/components/StatusBadge";
import { Receipt } from "lucide-react";

// Renders the BOGO payment breakdown from match.pricing (both free and discount schemes).
// `mode="recommended"` shows the suggested split; `mode="agreed"` shows the
// charge the partner will actually pay for the agreed fee.
export const PaymentBreakdown = ({ pricing, mode = "recommended" }) => {
  if (!pricing) return null;
  const buyerGets = mode === "recommended" ? pricing.recommended_fee : pricing.buyer_receives;
  const isDiscount = pricing.scheme === "bogo_discount";
  const isMultipack = pricing.scheme === "multipack";
  const lines = pricing.lines || [
    { label: "Goods share (½ of cost)", value: pricing.goods_share },
    { label: `Tax · ${pricing.tax_label}`, value: pricing.tax },
  ];
  const Row = ({ label, value, bold, testid }) => (
    <div className="flex items-center justify-between text-sm" data-testid={testid}>
      <span className={bold ? "font-semibold" : "text-muted-foreground"}>{label}</span>
      <span className={bold ? "font-heading" : ""} style={bold ? { fontWeight: 700 } : {}}>{value}</span>
    </div>
  );
  return (
    <div className="rounded-lg border border-black/10 bg-secondary/40 p-3.5 space-y-1.5" data-testid="payment-breakdown">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground/70">
        <Receipt className="h-3.5 w-3.5" /> Payment breakdown
        <span className="ml-auto text-[10px] font-normal text-muted-foreground">
          {isMultipack
            ? `Multi-pack · your share of ${money(pricing.original_price)}`
            : isDiscount
            ? `Savings ${money(pricing.savings)} on ${money(pricing.original_price)}`
            : `Cost of goods ${money(pricing.deal_price)}`}
        </span>
      </div>
      {lines.map((ln, i) => (
        <Row key={ln.testid || `pb-line-${i}`} label={ln.label} value={money(ln.value)} testid={ln.testid || `pb-line-${i}`} />
      ))}
      <div className="my-1 border-t border-black/5" />
      <Row label={isMultipack ? "Goes to owner (share + tax)" : "Buyer receives"} value={money(buyerGets)} bold testid="pb-buyer-receives" />
      <Row
        label={isMultipack ? "Flat fee (multi-pack)" : `Admin fee (${Math.round(pricing.admin_fee_pct * 100)}% of ${pricing.admin_fee_base})`}
        value={money(pricing.admin_fee)}
        testid="pb-admin-fee"
      />
      <div className="my-1 border-t border-black/5" />
      <Row label="Partner pays" value={money(pricing.total_partner_pays)} bold testid="pb-total" />
      {isMultipack && pricing.owner_flat_fee > 0 && (
        <p className="pt-0.5 text-[10px] text-muted-foreground" data-testid="pb-owner-note">
          Owner nets {money(pricing.owner_payout)} — a {money(pricing.owner_flat_fee)} flat fee is deducted from their payout.
        </p>
      )}
      <p className="pt-0.5 text-[10px] text-muted-foreground" data-testid="pb-processing-note">
        Card processing is covered by BogoShare — no surcharge is added to your total. Wallet payments also have no fee.
      </p>
    </div>
  );
};

export default PaymentBreakdown;
